import { useState } from 'react';
import type { JSX } from 'react';

import { currentIsoWeek, FACHLICHE_ZEITZONE } from '@asas/domain/iso-week';
import type { IsoWeek } from '@asas/domain';

import { anmeldeUrlMitRueckkehr } from '../daten/entwurf';
import { sperrtWeitereVersuche } from '../fachgruppen/fachgruppen';
import { trpc } from '../trpc';
import { wochenkopf } from '../wochenraster/wochenraster';

import { AuslastungTabelle } from './AuslastungAnsicht';
import {
  ANFANGSSTAND,
  AUSLASTUNG_WOCHEN,
  auslastungFensterVerschieben,
  erzeugeAuslastungSteuerung,
  MODUL_AUS_TEXT,
  naechsterStand,
} from './auslastung';
import type { AuslastungStand, OeffnenAusgang } from './auslastung';

/**
 * „Auslastung je Fachgruppe" (AC-407). Geladen wird erst auf Klick (keine
 * Effekte, Muster `WochenrasterBereich`): `auswertungen.fachgruppenSummen`
 * verlangt `resource_allocation:read_aggregated`, die Ansicht wird nur mit dem
 * Recht gezeigt (`ansichtSichtbar('auslastung')`).
 *
 * „Auslastung öffnen" prüft zuerst `tenant.settings` (Modul
 * `ressourcenplanung`); ist es aus, wird nichts abgerufen und der Knopf bleibt.
 * Früher/Später rufen direkt die Summen: ein Fensterwechsel bricht den
 * laufenden Aufruf ab (`erzeugeSummenLader`; die Prozedur läuft über die
 * eigene Link-Kette in `trpc.ts`, nur dort wirkt der `AbortSignal`). Ein
 * Fehler steht über der zuletzt geladenen Tabelle; der Kopf nennt die Wochen
 * der angezeigten Tabelle.
 */
export function AuslastungBereich(): JSX.Element {
  const [von, setVon] = useState<IsoWeek | null>(null);
  const [stand, setStand] = useState<AuslastungStand>(ANFANGSSTAND);
  const [laedt, setLaedt] = useState(false);
  const [steuerung] = useState(() =>
    erzeugeAuslastungSteuerung(
      async () => (await trpc.tenant.settings.query()).modules.ressourcenplanung === true,
      (from, optionen) =>
        trpc.auswertungen.fachgruppenSummen.query({ from, weeks: AUSLASTUNG_WOCHEN }, optionen),
    ),
  );

  const { summen, fehler, modulAus } = stand;
  const gesperrt = sperrtWeitereVersuche(fehler);
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  async function laden(ziel: IsoWeek, aufruf: (from: IsoWeek) => Promise<OeffnenAusgang>) {
    setVon(ziel);
    setLaedt(true);

    const ausgang = await aufruf(ziel);

    // Ein ueberholtes Fenster: das neuere raeumt `laedt` und den Stand.
    if (ausgang.art === 'ueberholt') return;
    setLaedt(false);
    setStand((alt) => naechsterStand(alt, ausgang));
  }

  const angezeigt = summen?.wochen[0];

  return (
    <section>
      <h2>Auslastung je Fachgruppe</h2>
      {fehler === null ? null : <p role="alert">{fehler.meldung}</p>}
      {fehler?.neuAnmelden === true ? (
        <p>
          <a href={anmeldeUrl}>Neu anmelden</a>
        </p>
      ) : null}
      {von === null || modulAus ? (
        <p>
          <button
            type="button"
            disabled={laedt || gesperrt}
            onClick={() => {
              void laden(currentIsoWeek(new Date(), FACHLICHE_ZEITZONE), steuerung.oeffnen);
            }}
          >
            Auslastung öffnen
          </button>
        </p>
      ) : (
        <p>
          <button
            type="button"
            disabled={gesperrt}
            onClick={() => {
              void laden(auslastungFensterVerschieben(von, -1), steuerung.wechseln);
            }}
          >
            Früher
          </button>{' '}
          {angezeigt === undefined ? '' : `ab ${wochenkopf(angezeigt, undefined)}`}{' '}
          <button
            type="button"
            disabled={gesperrt}
            onClick={() => {
              void laden(auslastungFensterVerschieben(von, 1), steuerung.wechseln);
            }}
          >
            Später
          </button>
        </p>
      )}
      <p role="status">{laedt ? 'Wird geladen …' : modulAus ? MODUL_AUS_TEXT : ''}</p>
      {summen === null ? null : (
        <>
          {summen.fachgruppen.length === 0 ? <p>Es gibt noch keine Fachgruppen.</p> : null}
          <AuslastungTabelle summen={summen} />
        </>
      )}
    </section>
  );
}
